import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdAssignment } from 'react-icons/md';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

// Contract tipleri için sabit değerler
const CONTRACT_TYPES = [
  { value: 'Support', label: 'Support' },
  { value: 'Project', label: 'Project' },
  { value: 'Managed Services', label: 'Managed Services' }
];

const SlaSettings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [slas, setSlas] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    sla_group_name: '',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedSla, setSelectedSla] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerContracts, setCustomerContracts] = useState([]);
  const [contractProducts, setContractProducts] = useState([]);
  const [assignFormData, setAssignFormData] = useState({
    customer_id: '',
    contract_id: '',
    product_id: ''
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      fetchData(userData.companyId);
    }
  }, []);

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="description"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  const fetchData = async (companyId) => {
    try {
      setLoading(true);
      const [slasResponse, productsResponse] = await Promise.all([
        api.get(`/sla/all/${companyId}`),
        api.get(`/products/all/${companyId}`)
      ]);

      if (slasResponse.data.success) {
        setSlas(slasResponse.data.slas);
      }
      if (productsResponse.data.success) {
        setProducts(productsResponse.data.products);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      showPopup({
        title: t('common.error'),
        message: t('messages.dataFetchError'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (slaId, description) => {
    showPopup({
      title: t('sla.deleteSla'),
      message: t('sla.confirmDeleteSla', { description }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/sla/${slaId}`);
          if (response.data.success) {
            setSlas(slas.filter(sla => sla.id !== slaId));
            showPopup({
              title: t('common.success'),
              message: t('slaSettings.slaDeletedSuccess'),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('slaSettings.errorDeletingSla'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleEdit = (sla) => {
    setFormData({
      sla_group_name: sla.sla_group_name,
      description: sla.description
    });
    setIsEditing(true);
    setEditingId(sla.id);
    setShowForm(true);
  };

  // Group name kontrolü için yeni fonksiyon
  const checkGroupName = async (name) => {
    try {
      const userStr = localStorage.getItem('user');
      const userData = JSON.parse(userStr);
      
      const response = await api.get(`/sla/check-group-name/${name}?company_id=${userData.companyId}`);
      if (response.data.exists) {
      showPopup({
        title: t('common.warning'),
        message: t('slaSettings.groupNameAlreadyExists'),
        type: 'error',
        showCancel: false
      });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Group name kontrolü sırasında hata:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sla_group_name.trim()) {
      showPopup({
        title: t('common.warning'),
        message: t('slaSettings.groupNameCannotBeEmpty'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    if (!isEditing && !(await checkGroupName(formData.sla_group_name))) {
      return;
    }

    try {
      const userStr = localStorage.getItem('user');
      const userData = JSON.parse(userStr);
      
      let response;
      if (isEditing) {
        response = await api.put(`/sla/${editingId}`, {
          ...formData,
          company_id: userData.companyId
        });
      } else {
        response = await api.post('/sla', {
          ...formData,
          company_id: userData.companyId
        });
      }

      if (response.data.success) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          await fetchData(userData.companyId);
        }

        setShowForm(false);
        setFormData({
          sla_group_name: '',
          description: ''
        });
        setIsEditing(false);
        setEditingId(null);

        showPopup({
          title: t('common.success'),
          message: isEditing ? t('slaSettings.slaUpdatedSuccess') : t('slaSettings.slaCreatedSuccess'),
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        showPopup({
          title: t('common.error'),
          message: error.response?.data?.message || t('slaSettings.errorOccurred'),
          type: 'error',
          showCancel: false
        });
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              value
    }));
  };

  // Popup yönetimi için fonksiyonlar
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Sayfalama hesaplamaları güncellendi
  const indexOfLastSla = currentPage * itemsPerPage;
  const indexOfFirstSla = indexOfLastSla - itemsPerPage;
  const currentSlas = slas.slice(indexOfFirstSla, indexOfLastSla);
  const totalPages = Math.ceil(slas.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Müşterileri getir
  const fetchCustomers = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/customers/${userData.companyId}`);
        if (response.data.success) {
          setCustomers(response.data.customers);
        }
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    }
  };

  // Müşteri seçildiğinde kontratları getir
  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    setAssignFormData({
      customer_id: customerId,
      contract_id: '',
      product_id: ''
    });
    setContractProducts([]);
    
    if (customerId) {
      try {
        const response = await api.get(`/customer-contracts/${customerId}`);
        if (response.data.success) {
          setCustomerContracts(response.data.contracts);
        }
      } catch (error) {
        console.error('Kontratlar yüklenirken hata:', error);
      }
    } else {
      setCustomerContracts([]);
    }
  };

  // Kontrat seçildiğinde ürünleri getir
  const handleContractChange = async (e) => {
    const contractId = e.target.value;
    setAssignFormData(prev => ({
      ...prev,
      contract_id: contractId,
      product_id: ''
    }));
    
    if (contractId) {
      try {
        const response = await api.get(`/products/${contractId}`);
        if (response.data.success) {
          setContractProducts(response.data.products);
        }
      } catch (error) {
        console.error('Ürünler yüklenirken hata:', error);
      }
    } else {
      setContractProducts([]);
    }
  };

  // SLA atama işlemini başlat
  const handleAssignClick = (sla) => {
    setSelectedSla(sla);
    setAssignFormData({
      customer_id: '',
      contract_id: '',
      product_id: ''
    });
    setShowAssignForm(true);
    fetchCustomers();
  };

  // SLA atama işlemini tamamla
  const handleAssignSubmit = async () => {
    try {
      const response = await api.put(`/products/${assignFormData.product_id}/assign-sla`, {
        sla_id: selectedSla.id
      });

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: t('slaSettings.slaAssignedSuccess'),
          type: 'success',
          showCancel: false
        });
        setShowAssignForm(false);
        
        // Company ID'yi al ve verileri yenile
        const userStr = localStorage.getItem('user');
        const userData = JSON.parse(userStr);
        fetchData(userData.companyId);
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('slaSettings.errorAssigningSla'),
        type: 'error',
        showCancel: false
      });
    }
  };

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('settings.slaGroups')}</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('sla.addNewSla')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Total SLA Groups: {slas.length}
        </span>
      </div>

      {/* SLA Table */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Sayfa boyutu seçici */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex justify-between items-center rounded-tl-lg rounded-tr-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/3">
                  {t('slaSettings.slaGroupName')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/3">
                  {t('slaSettings.description')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/6">
                  {t('slaSettings.createdAt')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-1/6">
                  {t('slaSettings.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentSlas.length > 0 ? (
                currentSlas.map((sla, index) => {
                  const isLast = index === currentSlas.length - 1 && totalPages <= 1;
                  return (
                    <tr 
                      key={sla.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer`}
                      onClick={() => navigate(`/settings/sla/${sla.sla_group_uuid || sla.id}`)}
                    >
                      {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 ${isLast ? 'rounded-bl-lg' : ''}`}>
                        <div className="text-sm  text-gray-900 dark:text-white">
                          {sla.sla_group_name}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          {sla.description}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          {new Date(sla.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 text-right ${isLast ? 'rounded-br-lg' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(sla);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(sla.id, sla.description);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <MdDelete size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignClick(sla);
                          }}
                          className="text-blue-600 hover:text-blue-900 ml-3"
                        >
                          <MdAssignment size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 rounded-bl-lg rounded-br-lg">
                    <div className="flex justify-center items-center ">
                      <p className="text-gray-400 dark:text-gray-500 text-sm ">{t('slaSettings.noSlaGroupsYet')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {currentSlas.length > 0 ? (
            currentSlas.map((sla, index) => (
              <div 
                key={sla.id} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => navigate(`/settings/sla/${sla.sla_group_uuid || sla.id}`)}
              >
                {/* SLA Group Name & Created At */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaSettings.slaGroupName')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{sla.sla_group_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaSettings.createdAt')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{new Date(sla.created_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Description & Actions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaSettings.description')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{sla.description}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('slaSettings.actions')}</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(sla);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(sla.id, sla.description);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <MdDelete size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssignClick(sla);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                      >
                        <MdAssignment size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 dark:text-gray-500 text-sm">{t('slaSettings.noSlaGroupsYet')}</p>
            </div>
          )}
        </div>

        {/* Pagination - Alt padding artırıldı */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstSla + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastSla, slas.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{slas.length}</span>{' '}
                  {t('slaSettings.slas')}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        currentPage === index + 1
                          ? 'bg-[#272c39] text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Popup - z-index'i artırıldı */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">
                {isEditing ? t('sla.editSla') : t('sla.addNewSla')}
              </h3>
              <button 
                onClick={() => setShowForm(false)}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            
            <div className="p-3 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaSettings.slaGroupName')}
                </label>
                <input
                  type="text"
                  value={formData.sla_group_name}
                  onChange={(e) => setFormData({ ...formData, sla_group_name: e.target.value })}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('slaSettings.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={2}
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                >
                  {isEditing ? t('slaSettings.updateSla') : t('slaSettings.addSla')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* SLA Atama Modal - Contract stili */}
      {showAssignForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-4">
          <div className="relative mx-auto w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header - Contract stili */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {t('slaSettings.assignSla')}: {selectedSla?.sla_group_name}
              </h3>
              <button 
                onClick={() => setShowAssignForm(false)}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">

            <div className="space-y-4">
              {/* Müşteri Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('slaSettings.selectCustomer')}
                </label>
                <select
                  value={assignFormData.customer_id}
                  onChange={handleCustomerChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                >
                  <option value="">{t('slaSettings.selectACustomer')}</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customer_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Kontrat Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('slaSettings.selectContract')}
                </label>
                <select
                  value={assignFormData.contract_id}
                  onChange={handleContractChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                  disabled={!assignFormData.customer_id}
                >
                  <option value="">{t('slaSettings.selectAContract')}</option>
                  {customerContracts.map(contract => (
                    <option key={contract.id} value={contract.id}>
                      {contract.contract_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ürün Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('slaSettings.selectProduct')}
                </label>
                <select
                  value={assignFormData.product_id}
                  onChange={(e) => setAssignFormData(prev => ({ ...prev, product_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md"
                  disabled={!assignFormData.contract_id}
                >
                  <option value="">{t('slaSettings.selectAProduct')}</option>
                  {contractProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.product_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAssignForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleAssignSubmit}
                  disabled={!assignFormData.product_id}
                  className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('slaSettings.assignSla')}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlaSettings; 